'use client';

import { type CSSProperties, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { SButton, type SButtonColor, type SButtonSize } from '../SButton';

export type SFooterBg = 'white' | 'grey';

export interface SFooterButton {
  label?: string;
  color?: SButtonColor;
  outline?: boolean;
  size?: SButtonSize;
  disabled?: boolean;
  onClick?: () => void;
}

/**
 * @internal
 * 모달·드로어·팝업 하단 영역 공통 props.
 */
export interface SFooterProps {
  /** 배경색 */
  bg?: SFooterBg;
  /** footer 좌측 slot */
  left?: ReactNode;
  /** footer 좌측 slot. left와 함께 쓰면 left 뒤에 렌더된다 */
  children?: ReactNode;
  /** 우측 기본 액션 버튼 */
  button?: SFooterButton;
  className?: string;
  leftClassName?: string;
  style?: CSSProperties;
}

/**
 * SFooter — overlay 계열 내부 전용 footer.
 *
 * @internal
 * SActionModal/SConfirmModal/SDrawer/SPopup 의 하단 액션 영역만 공통화한다.
 * 패키지 public export 에 추가하지 말 것.
 */
export function SFooter({
  bg = 'white',
  left,
  button,
  children,
  className,
  leftClassName,
  style,
}: SFooterProps) {
  const isGrey = bg === 'grey';
  const hasSlot = left != null || children != null;
  const hasButton = button != null;

  return (
    <footer
      className={cn(
        'relative flex w-full flex-shrink-0 items-center justify-end gap-[var(--cmp-overlay-footer-gap)] overflow-hidden px-[var(--cmp-overlay-footer-paddingX)] py-[var(--cmp-overlay-footer-paddingY)]',
        isGrey
          ? 'bg-[var(--cmp-overlay-footer-bg-neutral)] border-t border-solid border-[color:var(--sys-color-border-default)]'
          : 'bg-[var(--cmp-overlay-footer-bg-default)] shadow-[var(--shadow-spread-md)]',
        className,
      )}
      style={style}
    >
      {hasSlot && (
        <div
          className={cn('flex min-w-px flex-[1_0_0] items-center justify-between', leftClassName)}
        >
          {left}
          {children}
        </div>
      )}
      {hasButton && (
        <SButton
          color={button.color ?? 'primary'}
          outline={button.outline}
          size={button.size ?? 'md'}
          disabled={button.disabled}
          onClick={() => button.onClick?.()}
          label={button.label ?? '확인'}
        />
      )}
    </footer>
  );
}
